import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { breakpoint } from '../../../../grid';
import { Spinner } from '../../../../Spinner';
import { FontFamily } from '../../../../typography';
import { injectIntl, FormattedMessage } from 'react-intl';
import { renderRow } from '../Row';
import { handleFocus } from '../../HandleFocus';
import {
    LoadingContainer,
    SectionFooter,
    SectionSeparator,
    SectionHeader,
} from '../StyledDropdownComponents';
import { Link } from '../../../../link';
import { METRICS } from '../../../constants';

class BaseSearchView extends Component {
    static propTypes = {
        carriers: PropTypes.array,
        isLoading: PropTypes.bool,
        plans: PropTypes.array,
        clickHandler: PropTypes.func,
    };

    static defaultProps = {
        carriers: [],
        plans: [],
        clickHandler: () => {},
    }

    render() {
        const {
            isLoading,
        } = this.props;

        if (isLoading) {
            return (
                <div data-test="search-view">
                    <LoadingContainer data-test="loading-message">
                        <Spinner
                            inline
                            size={32}
                        />
                    </LoadingContainer>
                </div>
            );
        }

        return (
            <div data-test="search-view">
                {this.renderItems()}
                <SectionFooter />
            </div>
        );
    }

    renderItems() {
        const {
            carriers,
            plans,
            focusState,
        } = this.props;

        if (!carriers.length && !plans.length) {
            return this.renderNoMatches();
        }

        return (
            <SectionList innerRef={r => focusState.setScrollableArea(r)}>
                {this.renderTop()}
                {this.renderSeparator({ isCarriers: true })}
                {this.renderCarriers()}
                {this.renderSeparator({ isPlans: true })}
                {this.renderPlans()}
            </SectionList>
        );
    }

    renderSeparator({ isCarriers, isPlans }) {
        const {
            carriers,
            plans,
        } = this.props;

        if (isCarriers && !carriers.length) {
            return;
        }

        if (isPlans && !plans.length) {
            return;
        }

        return (<SectionSeparator data-test="section-separator" />);
    }

    renderNoMatches() {
        const {
            typedValue,
        } = this.props;

        return (
            <div data-test="no-results-view">
                <NoResultsHeader data-test="no-results-header">
                    <FormattedMessage
                        defaultMessage="Sorry no results found for "
                        id="Components.InsurancePicker.NoSearchResults"
                    />
                    &quot;{typedValue}&quot;
                </NoResultsHeader>
                <MoreLink
                    data-test="no-search-results-link"
                    onClick={() => this.props.textBoxChange({
                        target: {
                            value: '',
                        },
                    })}
                >
                    <FormattedMessage
                        defaultMessage="back"
                        id="Components.InsurancePicker.SeeAllCarriers"
                    />
                </MoreLink>
            </div>
        );
    }

    renderTop() {
        const {
            top,
            clickHandler,
            focusState,
        } = this.props;

        if (!top.length) {
            return;
        }

        return (
            <div data-test="search-top">
                <SectionHeader data-test="section-header">
                    <FormattedMessage
                        defaultMessage="top matches"
                        id="Components.InsurancePicker.Search.CarriersHeader.Top"
                    />
                </SectionHeader>

                <ol>
                    {top.map(t => {
                        const isPlan = Boolean(t.plan_name);
                        const action = isPlan ? METRICS.CHANGE_PLAN : METRICS.CHANGE_CARRIER;
                        const eventDetails = {
                            [isPlan ? METRICS.DETAILS_ID_FIELD.PLAN : METRICS.DETAILS_ID_FIELD.CARRIER]: t.id,
                            Name: isPlan ? t.plan_name : t.carrier_name,
                            SelectionType: isPlan ? METRICS.SELECTION_TYPE.PLAN : METRICS.SELECTION_TYPE.CARRIER,
                            TopMatch: true,
                        };

                        return renderRow(
                            t,
                            {},
                            clickHandler,
                            focusState,
                            null,
                            {
                                action,
                                element: 'Autocomplete',
                                eventDetails,
                            }
                        );
                    })
                    }
                </ol>
            </div>
        );
    }

    renderCarriers() {
        const {
            carriers,
            clickHandler,
            focusState,
        } = this.props;

        if (!carriers.length) {
            return;
        }

        return (
            <div data-test="search-carriers">
                <SectionHeader data-test="section-header">
                    <FormattedMessage
                        defaultMessage="matching carriers"
                        id="Components.InsurancePicker.Search.CarriersHeader.Carriers"
                    />
                </SectionHeader>
                <ol>
                    {carriers.map(t => renderRow(
                        t,
                        {},
                        clickHandler,
                        focusState,
                        null,
                        {
                            action: METRICS.CHANGE_CARRIER,
                            element: 'Autocomplete',
                            eventDetails: {
                                InsuranceCarrierId: t.id,
                                Name: t.carrier_name,
                                SelectionType: METRICS.SELECTION_TYPE.CARRIER,
                            },
                        }
                    ))}
                </ol>
            </div>
        );
    }

    renderPlans() {
        const {
            plans,
            clickHandler,
            focusState,
        } = this.props;

        if (!plans.length) {
            return;
        }

        return (
            <div data-test="search-plans">
                <SectionHeader data-test="section-header">
                    <FormattedMessage
                        defaultMessage="matching plans"
                        id="Components.InsurancePicker.Search.CarriersHeader.Plans"
                    />
                </SectionHeader>
                <ol>
                    {plans.map(t => renderRow(
                        t,
                        {},
                        clickHandler,
                        focusState,
                        null,
                        {
                            action: METRICS.CHANGE_PLAN,
                            element: 'Autocomplete',
                            eventDetails: {
                                InsurancePlanId: t.id,
                                Name: t.plan_name,
                                SelectionType: METRICS.SELECTION_TYPE.PLAN,
                            },
                        }
                    ))}
                </ol>
            </div>
        );
    }
}

const SectionList = styled.div`
    ${breakpoint.small} {
        max-height: 512px;
        overflow-y: auto;
        overflow-x: hidden;
    }
`;

const NoResultsHeader = styled.div`
    font: 15px/24px ${FontFamily.SharpSansBold};
    padding: 20px;
`;

const MoreLink = styled(Link)`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    margin: 4px 0 4px 20px;
`;

export const SearchView =
    injectIntl(
        handleFocus({
            shouldUpdate: (current, next) => (
                next.isLoading !== current.isLoading
                || next.top !== current.top
            ),
        })(
            BaseSearchView
        )
    );

